/**
 * requestedWorkflowHint — pure decision for the per-turn `requestedWorkflow`
 * dispatch hint (Routine Threads, 2026-07-31; thread-mirror condition added
 * 2026-08-01).
 *
 * Extracted from useGlobalTools so the routing contract is directly testable
 * (the hook itself is only exercised via source pins). One decision, three
 * rungs, in priority order:
 *
 *   1. ROUTINE THREAD (2026-08-01 — FIXED CONTRACT with the backend router):
 *      every dispatched turn whose ACTIVE (originating) thread carries a
 *      routineId sends `requestedWorkflow:'routines'`, REGARDLESS of message
 *      text. Production RCA: a user typed "resume" inside their paused
 *      routine's thread and the turn routed to the generic lane (the intent
 *      regex below can't match bare verbs by design) — routine-thread turns
 *      must always reach the routines lane.
 *   2. Explicit deep-research tool match (the turn-top regex router).
 *   3. Conservative routine-intent phrasing (EN/ES) for turns OUTSIDE routine
 *      threads — the original 2026-07-31 hint; the server-side detector
 *      (isRoutineIntentMessage) remains the wider safety net.
 */

/**
 * Deterministic client hint that this turn is a request to set up / manage a
 * recurring Routine (critical for dedicated-VM accounts, whose VM lane has no
 * routine tools). Deliberately conservative — a verb + article next to
 * "routine(s)" (EN/ES), plus the noun-first setup phrasing from the
 * 2026-07-31 production RCA ("a routine to set up" / "rutina que configurar",
 * which carries the noun BEFORE the verb so the verb-first alternations
 * missed it) — so "my morning routine"/"routine checkup" never match.
 */
export const ROUTINE_INTENT_HINT_RE =
  /(?:\b(?:set\s*up|create|make|schedule|add)\s+(?:a|an|another|the|my|this)\s+(?:new\s+|daily\s+|weekly\s+|monthly\s+|recurring\s+)?routines?\b)|(?:\b(?:pause|stop|cancel|update|resume|delete)\s+(?:the|this|that|my)\s+routines?\b)|(?:\bmy\s+routines\b)|(?:\broutines?\s+to\s+(?:set\s*up|create|make|schedule|add|start|test|try)\b)|(?:\b(?:configura(?:r)?|crea(?:r)?|programa(?:r)?)\s+(?:una|otra|la|mi)\s+(?:nueva\s+)?rutinas?\b)|(?:\bmis\s+rutinas\b)|(?:\brutinas?\s+(?:que|para)\s+(?:configurar|crear|programar|probar)\b)/i;

export interface RequestedWorkflowHintArgs {
  /**
   * The originating (active-at-send) thread's routineId, resolved through the
   * host-provided thread lookup — null/undefined when the thread carries no
   * routine, the lookup isn't wired, or the surface has no thread switcher.
   */
  threadRoutineId?: string | null;
  /** Turn-top regex router's matched tool id (e.g. 'deep-research'), if any. */
  topToolId?: string | null;
  /** The user's message text for this turn. */
  messageText: string;
}

/** Resolve the turn's `requestedWorkflow` dispatch hint (pure). */
export function resolveRequestedWorkflowHint(
  args: RequestedWorkflowHintArgs,
): 'deep-research' | 'routines' | undefined {
  // Rung 1 — routine thread: the fixed backend contract. Text is irrelevant;
  // "resume", "pause", "run it now", small talk — everything in a routine
  // thread lanes to the routines workflow.
  if (args.threadRoutineId) return 'routines';
  if (args.topToolId === 'deep-research') return 'deep-research';
  if (ROUTINE_INTENT_HINT_RE.test(args.messageText || '')) return 'routines';
  return undefined;
}
